import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { authService } from './authService';
import { appViewSetUser } from '../app/store/appViewSlice';
import { Dispatch } from 'redux';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// JWT token payload interface
interface TokenPayload {
  userId?: string;
  email?: string;
  inboxId?: string;
  exp?: number;
}

/**
 * Fetches and refreshes the user data in the Redux store
 * This handles both API fetching and fallback to token data
 */
export const refreshUserData = async (dispatch: Dispatch): Promise<boolean> => {
  const token = authService.getToken();
  if (!token) return false;

  try {
    console.log('Refreshing user data');

    // Get user data from API
    const response = await axios.get(`${API_URL}/api/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.data && response.data.email) {
      console.log('Successfully fetched user data from API');

      dispatch(
        appViewSetUser({
          first_name: response.data.firstName || '',
          last_name: response.data.lastName || '',
          email: response.data.email,
          image: response.data.image || '',
        }),
      );

      return true;
    }
  } catch (error) {
    console.error('Failed to fetch user data from API:', error);
  }

  // Fallback: Try to extract basic info from the token
  try {
    const decoded = jwtDecode<TokenPayload>(token);
    const email = decoded.email;
    const userId = decoded.userId;

    if (email) {
      console.log('Using fallback user data from token');
      const namePart = email.split('@')[0];

      dispatch(
        appViewSetUser({
          first_name: namePart,
          last_name: '',
          email: email,
          image: '',
        }),
      );

      return true;
    }
  } catch (error) {
    console.error('Failed to decode token:', error);
  }

  // If all attempts fail, clear token
  console.error('No user data available, clearing token');
  authService.clearToken();
  return false;
};

/**
 * Manually updates user profile data on the backend
 */
export const updateUserProfile = async (userData: {
  firstName: string;
  lastName: string;
  companyName?: string;
}): Promise<boolean> => {
  const token = authService.getToken();
  if (!token) return false;

  try {
    // Decode token to get userId
    const decoded = jwtDecode<TokenPayload>(token);
    const userId = decoded.userId;

    if (!userId) {
      console.error('No userId found in token');
      return false;
    }

    // Send update to backend
    console.log('Updating user profile on backend');
    const response = await axios.put(
      `${API_URL}/api/users/profile?userId=${userId}`,
      {
        firstName: userData.firstName,
        lastName: userData.lastName,
        preferences: userData.companyName
          ? { companyName: userData.companyName }
          : undefined,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    if (response.status === 200) {
      console.log('User profile updated on backend:', response.data);
      return true;
    } else {
      console.error('Failed to update profile on backend:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Failed to update user profile:', error);
    return false;
  }
};

export const userService = {
  refreshUserData,
  updateUserProfile,
};
